import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { buildRoutineTimeline } from '../utils/buildRoutineTimeline';
import type { UseRoutineTimelineProps, UseRoutineTimelineResult } from '../types/RoutineTimeline.types';
import { RoutineTimelineActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/routineTimeline/routineTimelineSlice';
import {
  getFinishedRouteCount,
  getRanRouteCount,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/routineTimeline/utils/routineProgress';
import { hasRunStoppedEarly } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/utils/taskStatus';
import {
  selectRoutineLastRouteName,
  selectRoutineTimeline,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/routineTimeline/routineTimelineSelectors';
import { isRobotCleaning } from 'app/modules/machine-inventory/utils/robotCleaningUtils';
import { useRobotCleaningProgressEntry } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningProgress/hooks/useRobotCleaningProgressEntry';

/**
 * The routine timeline of the robot's current routine run: the routine, its routes and the finished routes'
 * reports, together with the live task. The panel is wanted as soon as the task reports a route count, so
 * it reads as loading until the routine is there.
 */
export const useRoutineTimeline = ({
  robot,
  isActive,
  currentRoute,
  scheduleName,
}: UseRoutineTimelineProps): UseRoutineTimelineResult => {
  const dispatch = useDispatch();
  const task = useRobotCleaningProgressEntry(robot, isActive);
  const timeline = useSelector(selectRoutineTimeline);
  const lastRouteName = useSelector(selectRoutineLastRouteName);
  const isRobotCleaningNow = isRobotCleaning(robot);
  const { materialNumber, serialNumber, robotStatus } = robot;
  const taskId = task?.deviceTaskId;
  const isRoutineRun = (task?.stepCount ?? 0) > 0;
  const isReady = timeline.status === 'ready';
  const stoppedStep = taskId && timeline.taskId === taskId ? timeline.stoppedStep : null;
  const isStoppedEarly = hasRunStoppedEarly(
    task?.taskStatus,
    task?.step,
    task?.stepCount,
    task?.name,
    lastRouteName,
    isRobotCleaningNow,
    stoppedStep
  );
  // The task's step moves on after a stop, so the route is taken while the robot still reports cleaning.
  const cleaningStepRef = useRef<number | null>(null);
  // The run's routine number stays on the stopped route while the task's step moves on as the robot docks.
  const routine = robot.latestRoutine?.data;
  const isRoutineOfRun = !!taskId && routine?.executionId === taskId;
  const routineRepeat = isRoutineOfRun && (routine?.currentRepeat ?? 0) > 0 ? routine?.currentRepeat : null;
  // Once the run's routine has been seen, its end means the run is over even while the robot's status lags.
  const hasSeenRoutineRef = useRef(false);

  // A new run (a different task) loads its own routine.
  useEffect(() => {
    if (!isActive || !taskId || !isRoutineRun) {
      return undefined;
    }

    dispatch(RoutineTimelineActions.openRoutineTimelineRequest({ materialNumber, serialNumber }));

    return (): void => {
      dispatch(RoutineTimelineActions.closeRoutineTimeline());
    };
  }, [dispatch, isActive, taskId, isRoutineRun, materialNumber, serialNumber]);

  useEffect(() => {
    if (!isReady) {
      cleaningStepRef.current = null;
      hasSeenRoutineRef.current = false;
      return;
    }

    if (isRoutineOfRun) {
      hasSeenRoutineRef.current = true;
    }

    const hasRoutineEnded = hasSeenRoutineRef.current && !isRoutineOfRun;

    if (isRobotCleaningNow && !hasRoutineEnded) {
      cleaningStepRef.current = routineRepeat ?? task?.step ?? cleaningStepRef.current;

      if (timeline.stoppedStep != null) {
        dispatch(RoutineTimelineActions.routineStoppedStepChanged(null));
      }
    } else if (cleaningStepRef.current != null && timeline.stoppedStep == null) {
      dispatch(RoutineTimelineActions.routineStoppedStepChanged(cleaningStepRef.current));
    }
  }, [dispatch, isReady, isRobotCleaningNow, isRoutineOfRun, routineRepeat, task?.step, timeline.stoppedStep]);

  // The reports of the routes that are finished by now are wanted. The saga waits for the ones that have not arrived
  // yet, and starts over when another route finishes.
  // A run stopped early only has reports for the routes that ran.
  const finishedCount = isStoppedEarly
    ? getRanRouteCount({ reports: timeline.reports, stoppedStep: timeline.stoppedStep })
    : getFinishedRouteCount({
        step: task?.step,
        taskStatus: task?.taskStatus,
        totalRoutes: timeline.routine?.routeIds.length ?? 0,
        taskName: task?.name,
        lastRouteName,
        isRobotCleaning: isRobotCleaningNow,
      });

  useEffect(() => {
    if (!isReady || finishedCount === 0) {
      return;
    }

    dispatch(
      RoutineTimelineActions.refreshRoutineReportsRequest({
        materialNumber,
        serialNumber,
        finishedCount,
        isStoppedEarly,
      })
    );
    // A stopped run's report comes once the robot is home, so a status change asks for it again.
  }, [dispatch, isReady, finishedCount, isStoppedEarly, robotStatus, materialNumber, serialNumber]);

  const view = useMemo(
    () =>
      buildRoutineTimeline({
        timeline,
        task,
        currentRoute,
        scheduleName,
        isRobotCleaning: isRobotCleaningNow,
        isStoppedEarly,
      }),
    [timeline, task, currentRoute, scheduleName, isRobotCleaningNow, isStoppedEarly]
  );

  if (!isActive || !isRoutineRun || timeline.status === 'unavailable') {
    return { status: 'idle', view: null };
  }

  return { status: timeline.status === 'idle' ? 'loading' : timeline.status, view };
};
